import type { CategoryType } from "@habity/types";
import { ImageType, NestedHome } from "@habity/types";
import {
  getCategoryText,
  getCategoryTextDefinite,
  getCategoryTextPlural,
  getHomeAddress,
  getHomeAttributes,
  getLivingSpace,
  getRooms,
} from "../addresses";
import { getPrice } from "./prices";
import { capitalizeFirstLetter, formatCurrency } from "@habity/utils";

export const getOperationCost = (
  cost: number | null,
  monthly = false,
  currency = "SEK"
) => {
  if (cost === null) return "";
  let costValue = cost;
  if (monthly) {
    costValue = cost / 12;
  }
  costValue = Math.round(costValue);
  const formatted = new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency,
    maximumSignificantDigits: 5,
  }).format(costValue);
  return `${formatted} ${monthly ? "per månad" : "per år"}`;
};

export const getElectricityConsumption = (consumption: number | null) => {
  if (consumption === null) return "";
  const formatted = new Intl.NumberFormat("sv-SE", {
    maximumSignificantDigits: 3,
  }).format(consumption);
  return `${formatted} kWh per år`;
};

export const getHomeImageAlt = (home: NestedHome, image?: ImageType) => {
  const { street, city } = home.address;
  return image && image.name ? image.name : `${street}, ${city}`;
};

export const getHomesCount = (count: number | null = 0) => {
  if (!count && count !== 0) return "";
  let rounded = count;
  if (count > 1000) {
    rounded = Math.ceil(count / 500) * 500;
  }

  const formatted = new Intl.NumberFormat("sv-SE", {
    maximumSignificantDigits: 3,
  }).format(rounded);
  return count > 1000 ? formatted + "+" : formatted;
};

export const getSpeculantMessage = () => {
  return "";
};

export const isVerified = (home: NestedHome) => {
  const { is_claimed, images } = home || {};
  return is_claimed && images && images.length > 4;
};

/**
 * Check if a home has the seller_claimed status
 * @param home The home object to check
 * @returns boolean indicating if the home status is seller_claimed
 */
export const isSellerClaimed = (home: NestedHome) => {
  return home?.status?.name === "seller_claimed";
};

/**
 * Check if a home has the sold status
 * @param home The home object to check
 * @returns boolean indicating if the home status is sold
 */
export const isSold = (home: NestedHome) => {
  return home?.status?.name === "sold";
};

export const getHomeTitle = (home: NestedHome) => {
  const address = getHomeAddress(home);
  return `${address} - ${getHomeAttributes(home)}`;
};

export const getMortgagesAmount = (mortages: any[]) => {
  return mortages.reduce((acc: number, mortgage: any) => {
    return acc + mortgage.amount;
  }, 0);
};

export const getPropertyType = (
  isHousingCooperative: boolean,
  propertyType: string,
  providerType?: string
) => {
  if (isHousingCooperative) {
    if (providerType === "Condominium") return "Ägarlägenhet";
    return "Bostadsrätt";
  }
  return propertyType;
};

/**
 * Natural sentence describing the home for the speculator email body, to be
 * inserted after the intro sentence end. Order: category, rooms, living space,
 * monthly fee (if applicable), utropspris (if available).
 */
export const getHomeDetailsSentenceForEmail = (
  home: NestedHome | null
): string | null => {
  if (!home) return null;
  const { address, status, is_price_hidden, is_bidding_active } = home;
  const { category, rooms, living_space, additional_space, monthly_fee } =
    address ?? {};
  const segments: string[] = [];

  const categoryTextDefinite = category
    ? getCategoryTextDefinite(category as CategoryType)
    : "Bostaden är";
  segments.push(`${capitalizeFirstLetter(categoryTextDefinite)} är`);

  const roomsStr = rooms != null ? getRooms(rooms) : null;
  const spaceStr =
    living_space != null
      ? additional_space
        ? `${living_space} + ${additional_space} kvm`
        : `${living_space} kvm`
      : null;
  if (roomsStr && spaceStr) {
    segments.push(`på ${roomsStr} och ${spaceStr}`);
  } else if (roomsStr) {
    segments.push(`på ${roomsStr}`);
  } else if (spaceStr) {
    segments.push(`på ${spaceStr}`);
  }

  if (monthly_fee != null && monthly_fee > 0) {
    segments.push(`med månadskostnad på ${formatCurrency(monthly_fee, "SEK")}`);
  }

  if (status?.show_price && !is_price_hidden && home.price) {
    const priceStr = getPrice(home, is_bidding_active ?? false);
    if (priceStr && priceStr !== "Inget utropspris") {
      segments.push(`och utropspris ${priceStr}`);
    }
  }

  if (segments.length === 0) return null;
  return segments.join(" ") + ".";
};

/**
 * Same style as getHomeDetailsSentenceForEmail but from address-only data (no home/price).
 * Used for plaintext speculator emails when an address is selected but has no published home.
 */
export const getAddressDetailsSentenceForEmail = (address: {
  category?: string | null;
  rooms?: number | null;
  living_space?: number | null;
  additional_space?: number | null;
  monthly_fee?: number | null;
} | null): string | null => {
  if (!address) return null;
  const { category, rooms, living_space, additional_space, monthly_fee } = address;
  const segments: string[] = [];

  const categoryTextDefinite = category
    ? getCategoryTextDefinite(category as CategoryType)
    : "Bostaden är";
  segments.push(`${capitalizeFirstLetter(categoryTextDefinite)} är`);

  const roomsStr = rooms != null ? getRooms(rooms) : null;
  const spaceStr =
    living_space != null
      ? additional_space
        ? `${living_space} + ${additional_space} kvm`
        : `${living_space} kvm`
      : null;
  if (roomsStr && spaceStr) {
    segments.push(`på ${roomsStr} och ${spaceStr}`);
  } else if (roomsStr) {
    segments.push(`på ${roomsStr}`);
  } else if (spaceStr) {
    segments.push(`på ${spaceStr}`);
  }

  if (monthly_fee != null && monthly_fee > 0) {
    segments.push(`med månadskostnad på ${formatCurrency(monthly_fee, "SEK")}`);
  }

  if (segments.length <= 1 && segments[0]?.startsWith("Bostaden")) return null;
  return segments.join(" ") + ".";
};

export const getHomeDescription = (
  home: NestedHome,
  useDescription = true,
  showPrice = true
) => {
  const { address, description, description_generated, is_bidding_active } =
    home || {};
  const { city, category, rooms, living_space } = address || {};

  const text =
    (useDescription && description) ||
    (useDescription && description_generated) ||
    `${getCategoryText(category)}${city ? ` i ${city}` : ""}${
      living_space ? ` på ${living_space} kvm` : ""
    }${rooms ? ` och ${rooms} rum` : ""}${showPrice ? ` med pris ${getPrice(home, is_bidding_active)}` : ""}`;

  return text;
};
